<template>
  <div
    class="m-input"
    :class="{
      'm-input_success': success,
      'm-input_error': error,
      'm-input_small': small,
      'm-input_large': large,
      'm-input_disabled': disabled,
      'm-input_outset-icon': icon && outsetIcon,
      'm-input_inset-icon': icon && !outsetIcon
    }"
  >
    <slot name="label" />
    <label
      v-if="label"
      :for="_id"
      class="m-input__label"
    >
      {{ label }}
    </label>
    <div
      v-tooltip.right="hint ? {content: hint, show: showHint, trigger: 'manual'} : undefined"
      class="m-input__field-wrapper"
    >
      <input
        :id="_id"
        ref="input"
        :value="value"
        :title="label || placeholder"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :tabindex="tabIndex"
        :maxlength="maxlength"
        :name="name"
        class="input__field m-input__field"
        @change="onChange($event.target.value)"
        @input="onInput($event.target.value)"
        @focus="onFocus"
        @blur="onBlur"
        @click="onClick"
        @keyup.esc="onEscPressed"
      >
      <MIcon
        v-if="icon"
        :icon="icon"
        :title="label || placeholder"
        class="m-input__icon"
        @click.native="$emit('icon-click')"
      />
    </div>
    <div
      v-if="message"
      class="m-input__message"
    >
      {{ message }}
    </div>
  </div>
</template>

<script>
  import InputMixin from 'components/common/mixins/InputMixin';
  import MIcon from 'components/Design/MIcon';

  export default {
    name: 'MInput',
    components: {MIcon},
    mixins: [InputMixin],
    props: {
      type: {
        type: String,
        default: 'text'
      },
      value: {
        type: [Number, String],
        default: ''
      },
      autocomplete: {
        type: String,
        default: 'off'
      },
      maxlength: {
        type: Number,
        default: undefined
      },
      hint: {
        type: String,
        default: undefined
      },
      name: {
        type: String,
        default: ''
      },
      inputId: {
        type: String,
        default: ''
      }
    },
    data () {
      return {
        uid: null,
        showHint: false
      };
    },
    computed: {
      _id () {
        return this.inputId || this.uid;
      }
    },
    mounted () {
      this.uid = this._uid;
    },
    methods: {
      onChange (value) {
        if (this.emitInputOnChange) {
          this.$emit('input', value);

          /*
           * If you type something in an html input, the value attribute doesn’t change.
           * It also doesn’ change when you change the input’s content through the DOM API.
           * It’s always just a starting value, not the actual value, and Vue follows that behavior.
           * https://forum.vuejs.org/t/programatically-change-input-element-value/10365
           * that's why we need to re-render input to show actual `value` in DOM, not inner html input state
           */
          this.$forceUpdate();
        }
      },
      onBlur () {
        this.showHint = false;
        this.$emit('blur');
      },
      onFocus (e) {
        this.showHint = true;
        this.$emit('focus', e.target);
      },
      onClick (e) {
        this.$emit('click', e.target);
      },
      onInput (value) {
        if (!this.emitInputOnChange) {
          this.$emit('input', value);
        }
      },
      onEscPressed () {
        this.$refs.input.blur();
      }
    }
  };
</script>

<style>
  :root {
    /* TODO: move into .m-input selector */
    --input-width: 100%;
    --input-height: 32px;
    --input-padding: 8px;
    --input-margin: 8px;
    --input-icon-offset: 8px;
    --input-transition: 250ms ease-in-out;
    --input-message-spacing: 6px;
    --input-label-spacing: 8px;
    --input-small-height: 28px;
    --input-large-height: 40px;
  }

  .m-input {
    font-weight: var(--main-font-weight);

    display: flex;

    flex-direction: column;
  }

  .m-input_disabled {
    cursor: not-allowed;
  }

  .m-input__label {
    font-family: var(--main-font-family);
    font-size: var(--main-font-size);
    font-weight: var(--medium-font-weight);

    /* overriding application.css */
    color: var(--text-main-color);
    line-height: var(--main-line-height);

    margin-bottom: var(--input-label-spacing);
  }

  .m-input__field-wrapper {
    position: relative;
    display: flex;

    justify-content: space-between;
    align-items: center;
  }

  .m-input .m-input__field {
    font-family: var(--main-font-family);

    color: var(--secondary-text-color);

    box-sizing: border-box;
    width: var(--input-width);
    height: var(--input-height);

    padding-left: var(--input-padding);
    padding-right: var(--input-padding);

    /* deal with application.css */
    background: var(--background-color);

    border: solid 1px var(--border-color);

    border-radius: var(--border-radius);

    outline: none;

    transition: var(--input-transition);
  }

  .m-input__icon {
    flex-shrink: 0;

    width: var(--main-icon-size);
    height: var(--main-icon-size);
  }

  .m-input__message {
    margin-top: var(--input-message-spacing);
  }

  .m-input input {
    font-size: 14px;

    /* fix bootstrap styles */
    margin: 0;
  }

  /* stylelint-disable-next-line selector-no-qualifying-type */
  .m-input input[type='password'] {
    /* fix bootstrap styles */
    margin-bottom: 0;
  }

  .m-input_small input {
    font-size: 12px;
  }

  .m-input_small .m-input__field {
    height: var(--input-small-height);
  }

  .m-input_large .m-input__label,
  .m-input_large input {
    font-size: var(--subtitle-font-size);
    line-height: var(--subtitle-line-height);
  }

  .m-input_large .m-input__field {
    height: var(--input-large-height);
  }

  .m-input_disabled .m-input__field {
    color: var(--gray-text-color);

    cursor: not-allowed;

    background-color: var(--background-gray-color);

    border-color: var(--background-gray-color);
  }

  .m-input_inset-icon .m-input__field {
    padding-right: calc(var(--main-icon-size) + 2 * var(--input-padding));
  }

  .m-input_inset-icon .m-input__icon {
    position: absolute;
    right: var(--input-padding);
  }

  .m-input_outset-icon .m-input__field {
    margin-right: var(--input-icon-offset);
  }

  .m-input_success .m-input__field {
    border-color: var(--success-color);
  }

  .m-input_outset-icon .m-input_success .m-input__icon,
  .m-input_success .m-input__message {
    color: var(--success-color);
  }

  .m-error-wrapper__active .m-input__field,
  .m-input_error .m-input__field {
    border-color: var(--error-color);
  }

  .m-input_outset-icon .m-input_error .m-input__icon,
  .m-input_error .m-input__message {
    color: var(--error-color);
  }

  /* it's just a hot fix for placeholder */
  .m-input .m-input__field::-webkit-input-placeholder {  /* stylelint-disable-line selector-no-vendor-prefix */
    position: relative;
    top: 1px;
  }

  .m-input:not(.m-input_success):not(.m-input_error) .m-input__field:not(:disabled):focus,
  .m-input:not(.m-input_success):not(.m-input_error) .m-input__field:not(:disabled):hover {
    border-color: var(--action-color);
  }
</style>
